import React, { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Footer from './Footer';
import ChatButton from './ChatButton';
import PageInfo from './PageInfo';
import { useLocation } from 'react-router-dom';
import PageTransition from './PageTransition';
import WelcomePopup from './WelcomePopup';
import { ensureReferralInURL } from '@/utils/rewardfulUtils';
import PwaInstallButton from './PwaInstallButton';
import { useAuth } from '@/contexts/AuthContext';
import { useSentryError } from '@/hooks/useSentryError';
import SimpleOnboardingChecklist from './SimpleOnboardingChecklist';

interface LayoutProps {
  children: React.ReactNode;
  className?: string;
  pageInfo?: {
    title: string;
    description: string;
  };
}

const Layout: React.FC<LayoutProps> = ({ 
  children, 
  className = '',
  pageInfo 
}) => {
  const { user, onboardingCompleted } = useAuth();
  const { captureMessage, captureError } = useSentryError();
  const location = useLocation();
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  
  const defaultPageInfo = getDefaultPageInfo(location.pathname);
  const currentPageInfo = pageInfo || defaultPageInfo;
  
  const shouldShowOnboarding = user && !onboardingCompleted;
  
  useEffect(() => {
    try {
      // Log page navigation
      captureMessage(`Page visited: ${location.pathname}`, 'info', {
        component: 'Layout',
        action: 'pageNavigation',
        additionalData: { 
          pathname: location.pathname,
          userId: user?.id 
        }
      });

      if (sessionStorage.getItem('pwa-session-initialized') !== 'true') {
        console.log('Initializing new PWA session, resetting banner state');
        sessionStorage.removeItem('pwa-banner-closed');
        sessionStorage.setItem('pwa-session-initialized', 'true');
        
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.getRegistration('/sw.js').then(reg => {
            if (!reg) {
              console.log('Service worker not registered during session init, registering now');
              navigator.serviceWorker.register('/sw.js')
                .then(registration => console.log('ServiceWorker registered during session init:', registration.scope))
                .catch(error => {
                  console.error('Error registering ServiceWorker during session init:', error);
                  captureError(error, {
                    component: 'Layout',
                    action: 'serviceWorkerRegistration'
                  });
                });
            }
          });
        }
      }
      
      if (user) {
        const hasSeenWelcome = localStorage.getItem(`welcome-shown-${user.id}`);
        if (!hasSeenWelcome) {
          setShowWelcomePopup(true);
          localStorage.setItem(`welcome-shown-${user.id}`, 'true');
        }
      }
      
      ensureReferralInURL();
    } catch (error) {
      console.error('Error in Layout useEffect:', error);
      captureError(error as Error, {
        component: 'Layout',
        action: 'useEffect',
        additionalData: { pathname: location.pathname }
      });
    }
  }, [user, location, captureMessage, captureError]);
  
  return (
    <div className={`flex flex-col min-h-screen w-full ${className}`}>
      <PageTransition />
      <Navbar />
      <main className="flex-grow w-full relative">
        {currentPageInfo && <PageInfo title={currentPageInfo.title} description={currentPageInfo.description} />}
        {children}
      </main>
      <Footer />
      {user && <ChatButton />}
      <PwaInstallButton />
      
      {shouldShowOnboarding && <SimpleOnboardingChecklist />}
      
      {user && showWelcomePopup && (
        <WelcomePopup 
          open={showWelcomePopup} 
          onOpenChange={setShowWelcomePopup} 
          username={user.name || user.email?.split('@')[0] || 'usuario'}
        />
      )}
    </div>
  );
};

function getDefaultPageInfo(pathname: string) {
  const routes: Record<string, { title: string; description: string }> = {
    '/recetas': {
      title: 'Recetas Veganas',
      description: 'Explora nuestra colección de recetas veganas. Puedes filtrar por categoría, tiempo de preparación y valoración. Guarda tus favoritas y añádelas a tu menú semanal.'
    },
    '/menu-semanal': {
      title: 'Menú Semanal',
      description: 'Organiza tu menú semanal arrastrando recetas a los diferentes días. Puedes planificar desayunos, comidas y cenas para cada día de la semana.'
    },
    '/lista-compra': {
      title: 'Lista de Compra',
      description: 'Genera automáticamente una lista de compra basada en tu menú semanal. Puedes añadir o quitar ingredientes manualmente.'
    },
    '/favoritos': {
      title: 'Favoritos',
      description: 'Aquí encontrarás todas las recetas que has guardado como favoritas. Puedes filtrarlas y organizarlas según tus preferencias.'
    },
    '/nutricion': {
      title: 'Nutrición',
      description: 'Consulta información nutricional personalizada. Calcula tus necesidades calóricas y recibe recomendaciones basadas en tu perfil.'
    },
    '/diario-nutricional': {
      title: 'Diario Nutricional',
      description: 'Registra tu consumo diario de alimentos y visualiza estadísticas sobre tu nutrición. Controla tu ingesta de calorías, proteínas, grasas y carbohidratos.'
    },
    '/perfil': {
      title: 'Perfil de Usuario',
      description: 'Gestiona tu información personal, preferencias alimentarias y configuración de cuenta. Actualiza tu foto de perfil y datos de contacto.'
    },
    '/soporte': {
      title: 'Soporte',
      description: 'Contacta con nuestro equipo de soporte para resolver dudas o incidencias. Consulta las preguntas frecuentes antes de enviar un mensaje.'
    },
    '/suscripcion': {
      title: 'Suscripción',
      description: 'Gestiona tu plan de suscripción. Consulta las características de cada plan y actualiza tu método de pago.'
    },
    '/retos': {
      title: 'Retos',
      description: 'Participa en retos veganos divertidos para mejorar tu estilo de vida y ganar recompensas. Completa objetivos diarios y semanales.'
    }
  };

  return routes[pathname] || null;
}

export default Layout;
